import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import {
  enhanceRoutingInstructionsService,
  getDestinationOptionsService,
  saveCustomDestinationService,
} from "../../utils/customDestinationService";

export const MAX_ROUTING_INSTRUCTIONS_LENGTH = 1000;
export const MAX_FOLDER_NAME_LENGTH = 40;
export const MIN_FOLDER_NAME_LENGTH = 2;

// Mirrors the backend's ERROR_STATUS_BY_CODE contract
// (api/controllers/customDestinationController.js) with the UI-SPEC's exact
// copy per code. A code not in this table (including the generic 404
// ownership/unavailable fallback) uses the last, most conservative message.
const ERROR_COPY_BY_CODE = {
  CUSTOM_DESTINATION_LIMIT_REACHED: '5 of 5 custom folders configured. Remove one to add another.',
  CUSTOM_DESTINATION_DUPLICATE_RESOURCE: 'That folder is already linked to Geoffrey.',
  CUSTOM_DESTINATION_SNAPSHOT_CONFLICT: 'Folder settings changed. Refresh and try again.',
  CUSTOM_DESTINATION_RULE_GENERATION_FAILED: 'Could not generate routing rules. Your folders have not changed.',
  SYSTEM_MAPPING_INCOMPLETE: 'This mailbox has not finished initial setup yet. Try again shortly.',
  MAIL_DESTINATION_RESOURCE_NOT_FOUND: 'That folder is no longer available. Refresh and try again.',
  // Plan 02 (Create New Folder) additions.
  CUSTOM_DESTINATION_NAME_CONFLICT: 'A folder with that name already exists. Use Link Existing Folder to select it.',
  CUSTOM_DESTINATION_CREATE_AMBIGUOUS: 'The folder may have been created. Refresh Link Existing Folder before trying again.',
};
const DEFAULT_SAVE_ERROR_COPY = 'Could not save. Try again.';
const ENHANCE_ERROR_COPY = 'Could not enhance instructions. Your text is unchanged.';
const LOAD_ERROR_COPY = 'Could not load folders. Try again.';
const EMPTY_OPTIONS_COPY = 'No folders available to link. Create a new folder instead.';

const toSafeManagedDestinations = (managedDestinations) => (
  (managedDestinations ?? []).map(({ destinationKey, displayLabel }) => ({
    key: destinationKey,
    label: displayLabel,
  }))
);

// Custom Folders & Smart Email Routing (phase 1 plan 2). Create New Folder
// and Link Existing Folder share one card modal. Create is the default mode
// (01-UI-SPEC.md); the visible provider list is never fetched until Link is
// selected, and switching to Link always refreshes it -- both to keep Create
// a single bounded provider request and to give the ambiguous-create
// recovery path ("switch to Link Existing Folder") a guaranteed-fresh list.
const CustomFolderModal = ({
  isOpen,
  onClose,
  mailIntegrationId,
  getToken,
  onSaved,
}) => {
  const [mode, setMode] = useState('create'); // 'create' | 'link'
  const [loadState, setLoadState] = useState('idle'); // idle | loading | loaded | error
  const [options, setOptions] = useState([]);
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [folderName, setFolderName] = useState('');
  const [routingInstructions, setRoutingInstructions] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle | saving | error
  const [saveErrorMessage, setSaveErrorMessage] = useState(null);
  const [enhanceState, setEnhanceState] = useState('idle'); // idle | enhancing | error
  const [enhanceErrorMessage, setEnhanceErrorMessage] = useState(null);
  const firstFieldRef = useRef(null);
  const requestIdentityRef = useRef(null);

  // Per-open reset: capture THIS open's identity so a response that resolves
  // after the mailbox switches (or the dialog closes/reopens) never
  // overwrites a later, unrelated open's state.
  useEffect(() => {
    if (!isOpen) return;

    const identity = { mailIntegrationId };
    requestIdentityRef.current = identity;
    setMode('create');
    setLoadState('idle');
    setOptions([]);
    setSelectedResourceId('');
    setFolderName('');
    setRoutingInstructions('');
    setSaveState('idle');
    setSaveErrorMessage(null);
    setEnhanceState('idle');
    setEnhanceErrorMessage(null);

    return () => {
      // A mailbox switch or close mid-flight invalidates this open's
      // identity so any late response above is discarded.
      if (requestIdentityRef.current === identity) requestIdentityRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mailIntegrationId]);

  // Lazy load: the full visible provider list is fetched only once Link
  // Existing Folder is selected, and every switch into Link mode refreshes
  // it fresh (never a stale cached list, including for the ambiguous-create
  // recovery path). Deliberately keyed on [isOpen, mode] alone, NOT
  // mailIntegrationId: the reset effect above always forces mode back to
  // 'create' first whenever mailIntegrationId changes (a mailbox switch
  // resets the whole dialog), so by the time mode itself transitions to
  // 'link' again the mailIntegrationId closed over here is already current
  // -- this avoids a redundant automatic fetch racing the reset.
  useEffect(() => {
    if (!isOpen || mode !== 'link') return;

    const identity = requestIdentityRef.current;
    setLoadState('loading');
    setOptions([]);
    setSelectedResourceId('');

    getDestinationOptionsService(getToken, { mailIntegrationId })
      .then((result) => {
        if (requestIdentityRef.current !== identity) return;
        setOptions(Array.isArray(result?.options) ? result.options : []);
        setLoadState('loaded');
      })
      .catch(() => {
        if (requestIdentityRef.current !== identity) return;
        setLoadState('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]);

  useEffect(() => {
    if (isOpen && (mode !== 'link' || loadState === 'loaded')) {
      firstFieldRef.current?.focus();
    }
  }, [isOpen, mode, loadState]);

  if (!isOpen) return null;

  const trimmedInstructions = routingInstructions.trim();
  const instructionsTooLong = [...trimmedInstructions].length > MAX_ROUTING_INSTRUCTIONS_LENGTH;
  const trimmedFolderName = folderName.trim();
  const folderNameLength = [...trimmedFolderName].length;
  const folderNameTooLong = folderNameLength > MAX_FOLDER_NAME_LENGTH;
  const folderNameTooShort = folderNameLength > 0 && folderNameLength < MIN_FOLDER_NAME_LENGTH;
  const folderNameValid = folderNameLength >= MIN_FOLDER_NAME_LENGTH && !folderNameTooLong;
  const instructionsValid = trimmedInstructions.length > 0 && !instructionsTooLong;

  const canSubmit = (
    saveState !== 'saving'
    && enhanceState !== 'enhancing'
    && instructionsValid
    && (
      mode === 'create'
        ? folderNameValid
        : loadState === 'loaded' && selectedResourceId.length > 0
    )
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const handleModeChange = (nextMode) => {
    if (saveState === 'saving' || enhanceState === 'enhancing') return;
    setMode(nextMode);
  };

  const handleEnhance = async () => {
    if (!instructionsValid || saveState === 'saving' || enhanceState === 'enhancing') return;

    const identity = requestIdentityRef.current;
    setEnhanceState('enhancing');
    setEnhanceErrorMessage(null);
    try {
      const result = await enhanceRoutingInstructionsService(getToken, {
        routingInstructions: trimmedInstructions,
      });
      if (requestIdentityRef.current !== identity) return;
      setRoutingInstructions(result?.routingInstructions ?? trimmedInstructions);
      setEnhanceState('idle');
    } catch {
      if (requestIdentityRef.current !== identity) return;
      setEnhanceErrorMessage(ENHANCE_ERROR_COPY);
      setEnhanceState('error');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    // Duplicate Save dispatches one request: guarded by canSubmit (disabled
    // while saving) AND this explicit re-check against re-entrant submits.
    if (!canSubmit || saveState === 'saving') return;

    const identity = requestIdentityRef.current;
    setSaveState('saving');
    setSaveErrorMessage(null);

    try {
      const result = await saveCustomDestinationService(getToken, mode === 'create'
        ? { mailIntegrationId, mode: 'create', name: trimmedFolderName, routingInstructions: trimmedInstructions }
        : { mailIntegrationId, mode: 'link', resourceId: selectedResourceId, routingInstructions: trimmedInstructions });
      if (requestIdentityRef.current !== identity) return;

      onSaved?.({
        mailIntegrationId,
        destinations: toSafeManagedDestinations(result?.destinations),
      });
      onClose();
    } catch (error) {
      if (requestIdentityRef.current !== identity) return;
      const code = error?.response?.data?.code;
      setSaveErrorMessage(ERROR_COPY_BY_CODE[code] ?? DEFAULT_SAVE_ERROR_COPY);
      setSaveState('error');
    }
  };

  const selectableOptions = options.filter((option) => option.selectable);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[1100]"
      onKeyDown={handleKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-folder-modal-title"
        className="bg-white p-8 rounded-lg w-[90%] max-w-[600px] shadow-md max-h-[85vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 id="custom-folder-modal-title" className="m-0 text-xl font-semibold text-gray-800">
            Custom Folder
          </h2>
          <button
            type="button"
            className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 text-2xl cursor-pointer text-gray-600 hover:text-gray-800"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Describe which emails Geoffrey should route here.
        </p>

        <form onSubmit={handleSave} className="flex flex-col gap-4">
          <fieldset className="flex gap-4" disabled={saveState === 'saving' || enhanceState === 'enhancing'}>
            <legend className="sr-only">Custom Folder mode</legend>
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="radio"
                ref={firstFieldRef}
                name="custom-folder-mode"
                value="create"
                checked={mode === 'create'}
                onChange={() => handleModeChange('create')}
              />
              Create New Folder
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="radio"
                name="custom-folder-mode"
                value="link"
                checked={mode === 'link'}
                onChange={() => handleModeChange('link')}
              />
              Link Existing Folder
            </label>
          </fieldset>

          {mode === 'create' && (
            <div className="flex flex-col gap-1">
              <label htmlFor="custom-folder-name" className="text-sm font-medium text-gray-800">
                Folder name
              </label>
              <input
                id="custom-folder-name"
                type="text"
                value={folderName}
                onChange={(event) => setFolderName(event.target.value)}
                maxLength={MAX_FOLDER_NAME_LENGTH + 20}
                className="rounded-md border border-solid border-slate-200 text-sm p-2"
                aria-describedby={(folderNameTooLong || folderNameTooShort) ? 'custom-folder-name-error' : undefined}
              />
              {folderNameTooLong && (
                <p id="custom-folder-name-error" role="alert" className="text-xs text-red-600">
                  Folder name cannot exceed {MAX_FOLDER_NAME_LENGTH} characters.
                </p>
              )}
              {folderNameTooShort && (
                <p id="custom-folder-name-error" role="alert" className="text-xs text-red-600">
                  Folder name must be at least {MIN_FOLDER_NAME_LENGTH} characters.
                </p>
              )}
            </div>
          )}

          {mode === 'link' && loadState === 'loading' && (
            <p role="status" className="text-sm text-gray-600">Loading folders…</p>
          )}

          {mode === 'link' && loadState === 'error' && (
            <div role="alert" className="text-sm text-red-600">
              {LOAD_ERROR_COPY}
            </div>
          )}

          {mode === 'link' && loadState === 'loaded' && selectableOptions.length === 0 && (
            <p className="text-sm text-gray-600">{EMPTY_OPTIONS_COPY}</p>
          )}

          {mode === 'link' && loadState === 'loaded' && (
            <div className="flex flex-col gap-1">
              <label htmlFor="custom-folder-existing-select" className="text-sm font-medium text-gray-800">
                Existing folder
              </label>
              <select
                id="custom-folder-existing-select"
                value={selectedResourceId}
                onChange={(event) => setSelectedResourceId(event.target.value)}
                className="rounded-md border border-solid border-slate-200 text-sm p-2"
              >
                <option value="" disabled>Select a folder…</option>
                {options.map((option) => (
                  <option
                    key={option.resourceId}
                    value={option.resourceId}
                    disabled={!option.selectable}
                  >
                    {option.path}{!option.selectable && option.reason === 'built_in' ? ' (built-in)' : ''}
                    {!option.selectable && option.reason === 'already_managed' ? ' (already linked)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label htmlFor="custom-folder-routing-instructions" className="text-sm font-medium text-gray-800">
              Routing instructions
            </label>
            <textarea
              id="custom-folder-routing-instructions"
              value={routingInstructions}
              onChange={(event) => setRoutingInstructions(event.target.value)}
              disabled={enhanceState === 'enhancing' || saveState === 'saving'}
              maxLength={MAX_ROUTING_INSTRUCTIONS_LENGTH + 200}
              rows={3}
              className="rounded-md border border-solid border-slate-200 text-sm p-2"
              aria-describedby={instructionsTooLong ? 'custom-folder-instructions-error' : undefined}
            />
            {instructionsTooLong && (
              <p id="custom-folder-instructions-error" role="alert" className="text-xs text-red-600">
                Routing instructions cannot exceed {MAX_ROUTING_INSTRUCTIONS_LENGTH} characters.
              </p>
            )}
            <p className="text-xs text-gray-500">
              Describe it in your own words. Geoffrey will make the routing instructions clearer before saving.
            </p>
            <button
              type="button"
              onClick={handleEnhance}
              disabled={!instructionsValid || saveState === 'saving' || enhanceState === 'enhancing'}
              className="self-start rounded-md border border-sky-200 px-3 py-2 text-sm text-sky-700 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              {enhanceState === 'enhancing' ? 'Enhancing…' : 'Enhance with Geoffrey'}
            </button>
            {enhanceState === 'error' && (
              <p role="alert" className="text-xs text-red-600">{enhanceErrorMessage}</p>
            )}
          </div>

          {saveState === 'error' && (
            <div role="alert" className="text-sm text-red-600">
              {saveErrorMessage}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              className="py-3 px-6 rounded-md text-base cursor-pointer transition-colors duration-200 bg-gray-100 border border-gray-300 text-gray-800 hover:bg-gray-200"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`py-3 px-6 rounded-md text-base transition-colors duration-200 text-white ${
                canSubmit ? 'bg-sky-600 hover:bg-sky-700 cursor-pointer' : 'bg-sky-300 cursor-not-allowed'
              }`}
            >
              {saveState === 'saving' ? 'Saving Folder…' : 'Save Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomFolderModal;
